import type { Vec2, Vec3 } from '../geo/types';
import type { Pose, InputFrame } from './types';
import { FLY, WORLD, PLAYER } from '../core/config';
import { clamp, damp } from '../core/math';
import { worldToLayout } from '../core/coords';

export interface FlyState {
  readonly pose: Pose;
  readonly velocity: Vec3;
  readonly flySpeed: number;
}

export function createFlyState(pose: Pose, flySpeed: number = FLY.defaultSpeed): FlyState {
  return { pose, velocity: [0, 0, 0], flySpeed };
}

export function applyWheelToSpeed(currentSpeed: number, wheelSteps: number): number {
  const factor = Math.pow(FLY.wheelFactor, wheelSteps);
  return clamp(currentSpeed * factor, FLY.minSpeed, FLY.maxSpeed);
}

function computeTargetVelocity(input: InputFrame, yaw: number, pitch: number, speed: number): Vec3 {
  const cosYaw = Math.cos(yaw);
  const sinYaw = Math.sin(yaw);
  const cosPitch = Math.cos(pitch);
  const sinPitch = Math.sin(pitch);

  const forwardX = -sinYaw * cosPitch;
  const forwardY = sinPitch;
  const forwardZ = -cosYaw * cosPitch;

  return [
    (input.forward * forwardX + input.right * cosYaw) * speed,
    (input.forward * forwardY + input.up) * speed,
    (input.forward * forwardZ + input.right * -sinYaw) * speed,
  ];
}

function clampPosition(x: number, y: number, z: number, groundY: number): Vec3 {
  const minY = groundY + FLY.minClearance;
  const clampedY = clamp(y, minY, FLY.maxAltitude);

  const maxHorizontalDist = WORLD.farRingRadius * 0.5;
  const horizontalDist = Math.sqrt(x * x + z * z);

  if (horizontalDist > maxHorizontalDist) {
    const scale = maxHorizontalDist / horizontalDist;
    return [x * scale, clampedY, z * scale];
  }

  return [x, clampedY, z];
}

export function updateFly(
  state: FlyState,
  input: InputFrame,
  dt: number,
  groundHeightAt: (p: Vec2) => number
): FlyState {
  const { pose, velocity, flySpeed } = state;
  const newFlySpeed = applyWheelToSpeed(flySpeed, input.wheelSteps);

  const newYaw = pose.yaw - input.lookDx * PLAYER.lookRadPerPx;
  const newPitch = clamp(pose.pitch - input.lookDy * PLAYER.lookRadPerPx, -PLAYER.pitchLimit, PLAYER.pitchLimit);
  const newRoll = damp(pose.roll, 0, FLY.responseTime, dt);

  const speed = newFlySpeed * (input.boost ? FLY.boostFactor : 1);
  const targetVel = computeTargetVelocity(input, newYaw, newPitch, speed);

  const newVel: Vec3 = [
    damp(velocity[0], targetVel[0], FLY.responseTime, dt),
    damp(velocity[1], targetVel[1], FLY.responseTime, dt),
    damp(velocity[2], targetVel[2], FLY.responseTime, dt),
  ];

  const rawPos: Vec3 = [
    pose.position[0] + newVel[0] * dt,
    pose.position[1] + newVel[1] * dt,
    pose.position[2] + newVel[2] * dt,
  ];

  const groundY = groundHeightAt(worldToLayout(rawPos));
  const newPos = clampPosition(rawPos[0], rawPos[1], rawPos[2], groundY);

  return {
    pose: { position: newPos, yaw: newYaw, pitch: newPitch, roll: newRoll, fovYDeg: pose.fovYDeg },
    velocity: newVel,
    flySpeed: newFlySpeed,
  };
}
